import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCartCount } from '../utils/cartUtils';
import { hasSellerAccess, isAdmin, getSubscriptionBadge } from '../utils/subscriptionUtils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { 
  Menu, User, ShoppingBag, Package, LogOut, LayoutDashboard, 
  Star, Search, ChevronDown, DollarSign, HelpCircle, Users,
  Scan, BookOpen, MessageCircle, List, Heart, Gavel, Shield, CalendarDays, ShoppingCart
} from 'lucide-react';

export const Navbar = () => {
  const { user, logout, isAuthenticated, api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [cartCount, setCartCount] = useState(0);

  // Update cart count on mount and when cart changes
  useEffect(function() {
    setCartCount(getCartCount());
    function handleCartUpdate() {
      setCartCount(getCartCount());
    }
    window.addEventListener('cartUpdated', handleCartUpdate);
    return function() {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  // Fetch unread message count
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
      // Poll every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const fetchUnreadCount = async () => {
    try {
      const response = await api().get('/messages/unread-count');
      setUnreadMessages(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/marketplace?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const isActive = (path) => location.pathname === path;

  // Main navigation tabs
  const mainNavLinks = [
    { href: '/marketplace', label: 'Marketplace', icon: ShoppingBag },
    { href: '/auctions', label: 'Live Auctions', icon: Gavel, highlight: true },
    { href: '/community', label: 'Community', icon: CalendarDays },
    { href: '/sellers', label: 'Sellers Hub', icon: DollarSign },
    { href: '/scanner', label: 'AI Scanner', icon: Scan, requireAuth: true },
    { href: '/trust', label: 'Trust & Safety', icon: Shield },
    { href: '/about', label: 'About Us', icon: Users },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0b]/95 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 shrink-0"
            data-testid="nav-logo"
          >
            <img 
              src={process.env.REACT_APP_LOGO_URL} 
              alt="Slab Society"
              className="w-10 h-10 rounded-xl object-contain"
            />
            <span className="font-heading font-bold text-xl tracking-tight hidden lg:block text-white">
              SLAB SOCIETY
            </span>
          </Link>

          {/* Search Bar - Prominent */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-4">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="text"
                placeholder="Search cards, players, sets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 h-10 bg-zinc-900/80 border-zinc-700 rounded-full text-sm placeholder:text-zinc-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                data-testid="nav-search"
              />
            </div>
          </form>

          {/* Desktop Nav Tabs - Hidden on mobile */}
          <div className="desktop-nav-links hidden lg:flex items-center gap-1">
            {mainNavLinks.map((link) => {
              if (link.requireAuth && !isAuthenticated) return null;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                    isActive(link.href) 
                      ? 'text-blue-400 bg-blue-500/10' 
                      : link.highlight
                        ? 'text-red-400 hover:text-white hover:bg-red-500/10'
                        : 'text-zinc-400 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.highlight && (
                    <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse mr-1.5" />
                  )}
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* User Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Cart Icon - Link to Cart Page */}
            {isAuthenticated && (
              <Link 
                to="/cart" 
                className="relative p-2 text-zinc-400 hover:text-white transition-colors"
                data-testid="nav-cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-emerald-500 text-white text-xs font-bold rounded-full">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </Link>
            )}
            
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 text-zinc-300 hover:text-white px-2"
                    data-testid="user-menu-trigger"
                  >
                    {user?.profile_picture ? (
                      <img 
                        src={user.profile_picture.startsWith('/api') 
                          ? `${process.env.REACT_APP_BACKEND_URL}${user.profile_picture}` 
                          : user.profile_picture} 
                        alt={user?.name}
                        className="w-8 h-8 rounded-full object-cover border border-blue-500/30"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-400" />
                      </div>
                    )}
                    <span className="font-medium hidden sm:block">{user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-4 h-4 text-zinc-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 bg-[#18181B] border-white/10 p-2">
                  {/* User Info Header */}
                  <div className="px-3 py-3 mb-2 bg-zinc-900/50 rounded-lg">
                    <p className="font-semibold text-white">{user?.name}</p>
                    <p className="text-sm text-zinc-400">@{user?.username}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {user?.membership_status === 'Creator' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">Creator</span>
                      )}
                      {hasSellerAccess(user) && user?.membership_status !== 'Creator' && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          user?.subscription_status === 'trialing' 
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                            : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                        }`}>
                          {user?.subscription_status === 'trialing' ? 'Trial' : 'Seller'}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-3 cursor-pointer py-2.5" data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4 text-zinc-400" />
                      <span>Dashboard</span>
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem asChild>
                    <Link to={`/profile/${user?.id}`} className="flex items-center gap-3 cursor-pointer py-2.5" data-testid="menu-profile">
                      <User className="w-4 h-4 text-zinc-400" />
                      <span>My Profile</span>
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem asChild>
                    <Link to="/orders" className="flex items-center gap-3 cursor-pointer py-2.5" data-testid="menu-orders">
                      <ShoppingBag className="w-4 h-4 text-zinc-400" />
                      <span>My Orders</span>
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem asChild>
                    <Link to="/my-listings" className="flex items-center gap-3 cursor-pointer py-2.5" data-testid="menu-my-listings">
                      <List className="w-4 h-4 text-zinc-400" />
                      <span>My Listings</span>
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem asChild>
                    <Link to="/watchlist" className="flex items-center gap-3 cursor-pointer py-2.5" data-testid="menu-watchlist">
                      <Heart className="w-4 h-4 text-zinc-400" />
                      <span>My Watchlist</span>
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem asChild>
                    <Link to="/messages" className="flex items-center gap-3 cursor-pointer py-2.5" data-testid="menu-messages">
                      <div className="relative">
                        <MessageCircle className="w-4 h-4 text-zinc-400" />
                        {unreadMessages > 0 && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
                        )}
                      </div>
                      <span className="flex-1">Messages</span>
                      {unreadMessages > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500 text-white">
                          {unreadMessages}
                        </span>
                      )}
                    </Link>
                  </DropdownMenuItem>
                  
                  {/* Admin Dashboard - only for admins/superusers */}
                  {isAdmin(user) && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin-dashboard" className="flex items-center gap-3 cursor-pointer py-2.5 text-amber-400" data-testid="menu-admin-dashboard">
                        <Shield className="w-4 h-4" />
                        <span>Admin Dashboard</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  
                  {hasSellerAccess(user) && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/sales" className="flex items-center gap-3 cursor-pointer py-2.5" data-testid="menu-sales">
                          <Package className="w-4 h-4 text-zinc-400" />
                          <span>My Sales</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/collection" className="flex items-center gap-3 cursor-pointer py-2.5" data-testid="menu-collection">
                          <BookOpen className="w-4 h-4 text-zinc-400" />
                          <span>My Collection</span>
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  
                  <DropdownMenuSeparator className="bg-white/5 my-2" />
                  
                  {!hasSellerAccess(user) && (
                    <DropdownMenuItem asChild>
                      <Link to="/subscription" className="flex items-center gap-3 cursor-pointer py-2.5 text-blue-400" data-testid="menu-subscribe">
                        <Star className="w-4 h-4" />
                        <span>Become a Seller</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="flex items-center gap-3 cursor-pointer py-2.5 text-red-400 focus:text-red-400"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-zinc-300 hover:text-white" data-testid="nav-login">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="nav-register">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Trigger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" className="ml-1" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-[#09090B] border-white/10 p-0">
                <div className="flex flex-col h-full">
                  {/* Mobile Search */}
                  <div className="p-4 border-b border-white/5">
                    <form onSubmit={(e) => { handleSearch(e); setMobileOpen(false); }}>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input
                          type="text"
                          placeholder="Search cards..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 bg-zinc-900 border-zinc-700 rounded-lg"
                        />
                      </div>
                    </form>
                  </div>
                  
                  {/* Mobile Nav Links */}
                  <div className="flex-1 overflow-y-auto py-4">
                    {mainNavLinks.map((link) => {
                      if (link.requireAuth && !isAuthenticated) return null;
                      const Icon = link.icon;
                      return (
                        <Link
                          key={link.href}
                          to={link.href}
                          onClick={() => setMobileOpen(false)}
                          className={`flex items-center gap-3 px-6 py-3.5 text-base ${
                            isActive(link.href) 
                              ? 'text-blue-400 bg-blue-500/10' 
                              : 'text-zinc-300 hover:bg-white/5'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          {link.label}
                        </Link>
                      );
                    })}
                  </div>
                  
                  {/* Mobile User Section */}
                  <div className="border-t border-white/5 p-4">
                    {isAuthenticated ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 pb-3 border-b border-white/5">
                          {user?.profile_picture ? (
                            <img 
                              src={user.profile_picture.startsWith('/api') 
                                ? `${process.env.REACT_APP_BACKEND_URL}${user.profile_picture}` 
                                : user.profile_picture} 
                              alt={user?.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
                              <User className="w-5 h-5 text-blue-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-white">{user?.name}</p>
                            <p className="text-sm text-zinc-400">@{user?.username}</p>
                          </div>
                        </div>
                        <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                          <Button className="w-full bg-blue-600 hover:bg-blue-500">Dashboard</Button>
                        </Link>
                        <Button onClick={handleLogout} variant="outline" className="w-full border-zinc-700 text-red-400 hover:text-red-300">
                          Sign Out
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Link to="/login" onClick={() => setMobileOpen(false)}>
                          <Button variant="outline" className="w-full border-zinc-700">Sign In</Button>
                        </Link>
                        <Link to="/register" onClick={() => setMobileOpen(false)}>
                          <Button className="w-full bg-blue-600 hover:bg-blue-500">Get Started</Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
